import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { navigateTo } from '@/Utils/routing';
import { LoadingView } from '@/Components/Topik/LoadingView';
import { ErrorView } from '@/Components/Topik/ErrorView';
import { StartView } from '@/Components/Topik/StartView';
import { QuestionGroup } from '@/Components/Topik/QuestionGroup';
import { AudioPlaybackInfo } from '@/Components/Topik/Listening/AudioPlaybackInfo';
import { Timer } from '@/Components/Topik/Timer';
import './index.scss';
import '@/Components/Topik/TestHeader.scss';
import { useAttempts, useQuestionnaires } from '@/Recoil/Topik';
// Section timeouts in minutes
const SECTION_TIMEOUTS = {
    listening: 60,  // 60 minutes
    reading: 70,    // 70 minutes
    writing: 50     // 50 minutes
};

// Noise audio URL for gap between questions
const NOISE_URL = 'https://static.koreanmate.app/noise.wav';

// Styles
const OVERLAY_STYLE = {
    position: 'fixed' as const,
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999
};

const MODAL_STYLE = {
    backgroundColor: 'white',
    padding: '32px',
    borderRadius: '8px',
    textAlign: 'center' as const
};

interface TopikTestProps {
    questionnaireid: string;
    attemptid: string;
    section: 'listening' | 'reading' | 'writing';
}

function TopikTest({ questionnaireid, attemptid, section }: TopikTestProps) {

    const {examContent, getQuestionnaire} = useQuestionnaires();
    const {attempts, getAttempt, submitAnswer, answers, startAttempt, submitAttempt} = useAttempts();

    const questionnaire = useMemo(() => examContent[`${questionnaireid}_${section}`], [examContent, questionnaireid, section]);
    const attempt = useMemo(() => attempts[attemptid], [attempts, attemptid]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isStarted, setIsStarted] = useState(false);
    const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    
    // Local state for answers to avoid API calls on every change
    const [localAnswers, setLocalAnswers] = useState<Record<number, string>>({});
    
    // State for submission pending
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Initialize localAnswers with existing selectedAnswers
    useEffect(() => {
        setLocalAnswers(answers[attemptid]);
    }, [attemptid, answers]);


    const audioList = useMemo(() => {
        if (!questionnaire || section !== 'listening') return [];
        
        const audios: string[] = [];
        const questionGroups = questionnaire.listeningQuestionGroups;
        
        questionGroups.forEach((group, groupIndex) => {
            // Add shared audio if exists
            if (group.shared_resource?.type === 'audio' && group.shared_resource.audio_url) {
                audios.push(group.shared_resource.audio_url);
                // Add noise after shared audio
                audios.push(NOISE_URL);
            }
            
            // Add individual question audios
            group.questions.forEach((question, questionIndex) => {
                if (question.audio?.audio_url) {
                    audios.push(question.audio.audio_url);
                    // Add noise after each question audio (except the last one)
                    const isLastQuestion = groupIndex === questionGroups.length - 1 && 
                                         questionIndex === group.questions.length - 1;
                    if (!isLastQuestion) {
                        audios.push(NOISE_URL);
                    }
                }
            });
        });
        
        return audios;
    }, [questionnaire, section]);

    const audioNameList = useMemo(() => {
        if (!questionnaire || section !== 'listening') return [];
        
        const audioNames: string[] = [];
        const questionGroups = questionnaire.listeningQuestionGroups;
        
        questionGroups.forEach((group, groupIndex) => {
            // Add shared audio if exists
            if (group.shared_resource?.type === 'audio' && group.shared_resource.audio_url) {
                audioNames.push('shared audio for ' + group.groupid);
                // Add noise after shared audio
                audioNames.push('5-second pause');
            }
            
            // Add individual question audios
            group.questions.forEach((question, questionIndex) => {
                if (question.audio?.audio_url) {
                    audioNames.push(`audio for question ${question.questionid}`);
                    // Add noise after each question audio (except the last one)
                    const isLastQuestion = groupIndex === questionGroups.length - 1 && 
                                         questionIndex === group.questions.length - 1;
                    if (!isLastQuestion) {
                        audioNames.push('5-second pause');
                    }
                }
            });
        });

        return audioNames;
    }, [questionnaire, section]);

    useEffect(() => {
        if (audioList.length === 0) return;
        if (currentAudioIndex >= audioList.length) return;

        // Clean up previous audio
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }

        // Create and play new audio
        const audio = new Audio(audioList[currentAudioIndex]);
        audioRef.current = audio;

        // Set up event handlers
        audio.onended = () => {
            setCurrentAudioIndex(prev => prev + 1);
        };

        audio.onerror = (e) => {
            console.error('Audio playback error:', e);
            setCurrentAudioIndex(prev => prev + 1);
        };

        if (currentAudioIndex > 0){
            audio.play().catch(err => {
                console.error('Failed to play audio:', err);
                setCurrentAudioIndex(prev => prev + 1);
            });
        }

        // Cleanup function
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, [audioList, currentAudioIndex]);

    const fetchData = useRef(async () => {
        try {
            setLoading(true);

            await getQuestionnaire({questionnaireid, section})
            await getAttempt(attemptid);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Error loading test data');
        } finally {
            setLoading(false);
        }
    });

    useEffect(() => {
        fetchData.current();
    }, [questionnaireid, attemptid, section]);

    const handleStartTest = async () => {
        try {
            if (attempt.status === 'pending') {
                const result = await startAttempt(attemptid);
                if (result) {
                    setIsStarted(true);
                } else {
                    alert('Failed to start test. Please try again.');
                }
            } else if (attempt.status === 'active') {
                setIsStarted(true);
            }

            // Play audio
            audioRef.current?.play().catch(err => {
                console.error('Failed to play audio:', err);
                setCurrentAudioIndex(prev => prev + 1);
            });
        } catch (error) {
            console.error('Error starting test:', error);
            alert('Error starting test. Please try again.');
        }
    };

    const handleAnswerSelect = useCallback(async (questionId: number, answer: string) => {
        // Update local state
        setLocalAnswers(prev => ({
            ...prev,
            [questionId]: answer
        }));
        
        // For writing section, don't submit to backend on every change
        if (section === 'writing') {
            return;
        }
        
        // For other sections, submit answer to backend
        try {
            await submitAnswer({
                attemptid,
                questionid: questionId,
                answer
            });
        } catch (error) {
            console.error('Error submitting answer:', error);
        }
    }, [attemptid, section, submitAnswer]);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            // For writing section, save all answers first
            if (section === 'writing') {
                // Submit all local answers that haven't been saved yet
                const writingAnswers = Object.entries(localAnswers);
                for (const [questionId, answer] of writingAnswers) {
                    try {
                        await submitAnswer({
                            attemptid,
                            questionid: parseInt(questionId),
                            answer
                        });
                    } catch (error) {
                        console.error(`Error submitting answer for question ${questionId}:`, error);
                    }
                }
            }
            
            // Prepare answers in the format expected by the API
            const answers = Object.entries(localAnswers).map(([questionid, answer]) => ({
                questionid: parseInt(questionid),
                answer
            }));

            // Submit the attempt with all answers
            const result = await submitAttempt(attemptid, answers);
            
            if (result) {
                // Navigate to the result page
                navigateTo(`/topik/${questionnaireid}/${attemptid}/${section}/result`);
            } else {
                setIsSubmitting(false);
                alert('Failed to submit test. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting test:', error);
            setIsSubmitting(false);
            alert('Error submitting test. Please try again.');
        }
    };

    // Get current question groups based on section
    const questionGroups = useMemo(() => 
        !questionnaire ? [] :
        section === 'listening' ? questionnaire.listeningQuestionGroups :
        section === 'reading' ? questionnaire.readingQuestionGroups :
                questionnaire.writingQuestionGroups
    , [section, questionnaire]);

    if (loading) {
        return <LoadingView />;
    }

    if (error || !questionnaire) {
        return <ErrorView error={error} />;
    }

    if (!isStarted) {
        return <StartView section={section} status={attempt.status} onStart={handleStartTest} />;
    }

    return (
        <div className="TopikTest">
            {isSubmitting && (
                <div style={OVERLAY_STYLE}>
                    <div style={MODAL_STYLE}>
                        <div style={{ fontSize: '18px', marginBottom: '16px', color: '#333' }}>
                            제출 중입니다...
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                            잠시만 기다려주세요
                        </div>
                    </div>
                </div>
            )}
            <div className="TestHeader">
                <div className="HeaderContent">
                    <div className="LeftSection">
                        <div className="SectionTitle">
                            {section.charAt(0).toUpperCase() + section.slice(1)}
                        </div>
                        <Timer 
                            startedAt={attempt?.started_at}
                            totalSeconds={SECTION_TIMEOUTS[section] * 60}
                        />
                    </div>
                    {section === 'listening' && (
                        <AudioPlaybackInfo 
                            currentIndex={currentAudioIndex}
                            currentAudioName={audioNameList[currentAudioIndex] || ''}
                            totalCount={audioList.length}
                            isPlaying={currentAudioIndex < audioList.length}
                        />
                    )}
                    <button className="SubmitButton" onClick={handleSubmit} disabled={isSubmitting}>
                        Submit
                    </button>
                </div>
            </div>

            <div className="BodyContainer">
                {questionGroups.map((group) => (
                    <QuestionGroup
                        key={group.groupid}
                        group={group}
                        section={section}
                        selectedAnswers={JSON.stringify(
                            Object.entries(localAnswers)
                                .filter(([questionid]) => group.questions.some(q => q.questionid === parseInt(questionid)))
                                .reduce((acc, [questionid, answer]) => ({ ...acc, [questionid]: answer }), {})
                        )}
                        onAnswerSelect={handleAnswerSelect}
                    />
                ))}
            </div>
        </div>
    );
}

export default TopikTest;